import { useQuery } from '@tanstack/react-query'
import { ApiQueryPromise } from '@/types';
import { getResponseJwtHeader } from '@/utils/web';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import queryKeys from '@/constants/query-keys';
import { useAccountInfo } from '@/hooks/query/account';
import { useBetslipStore } from '@/store/betslip';
import { useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import { useSiteStore } from '@/store/site';
import { getAuthToken, useAuthActions } from '@/store/auth';
import { cloudUrlParser } from '@/utils/aws-domain-util';
import { UserInfo } from '@nf/types/account';
import { useRouter } from 'next/navigation';
import type {
	BetTicketsResponse,
	ParlayTicketModel,
	ParlayTicketsModel,
	SyncTicketsResponse,
	TicketModel,
} from './types';
import { BetAction } from './types';
import { BetslipService } from '@/services/betslip.service';

/** 呼叫 API - GetComboTickets */
const syncBBPlusTicket = async (tickets: Array<TicketModel>, useSabaCoin?: boolean): Promise<ApiQueryPromise<SyncTicketsResponse>> => {
	const authToken = getAuthToken();
	const { ctcdDomain } = useSiteStore.getState().apiDomain;

	let formData = new FormData();

	formData = BetslipService.convertTicketToFormData(formData, tickets, 'ItemList');
	formData.append('betAction', BetAction.BetBuilderPlus.toString());

	if (useSabaCoin) {
		formData.append('isUseSabaCoin', 'true');
	}

	const options = {
		body: formData
	};

	const response = await fetchComposer.postWithBearerBase(cloudUrlParser(`${ctcdDomain}/api/Betting/GetComboTickets`), options)(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

const betBBPlusTicket = async (
	ctcdDomain: string,
	parlayData: ParlayTicketsModel | null,
	user: UserInfo | null | undefined,
	useSabaCoin: boolean,
): Promise<ApiQueryPromise<BetTicketsResponse>> => {
	const authToken = getAuthToken();
	const { betAction } = useBetslipStore.getState();
	let formData = new FormData();
	let bettingParlayTickets: ParlayTicketModel[] = [];

	if (parlayData?.Tickets?.length) {
		bettingParlayTickets = parlayData.comboData?.filter(ticket => ticket.CanBet && ticket.stake);
	}

	if (bettingParlayTickets.length) {
		formData = BetslipService.convertTicketToFormData(formData, parlayData?.Tickets ?? [], 'comboData[ItemList]');
		bettingParlayTickets?.forEach((parlayTicket, index) => {
			formData.append(`comboData[ComboLists][${index}][Type]`, parlayTicket.Type.toString());
			formData.append(`comboData[ComboLists][${index}][BetCount]`, parlayTicket.BetCount.toString());
			formData.append(`comboData[ComboLists][${index}][Stake]`, parlayTicket.stake.toString());
		});
	}

	// leg odds 因為BB+ 而加上的
	if (parlayData?.LegOdds?.length) {
		parlayData.LegOdds.forEach((legOdd, index) => {
			formData.append(`comboData[LegOdds][${index}][ErrorCode]`, legOdd.ErrorCode.toString());
			formData.append(`comboData[LegOdds][${index}][ErrorMessage]`, legOdd.ErrorMessage || '');
			formData.append(`comboData[LegOdds][${index}][MatchId]`, legOdd.MatchId.toString());
			formData.append(`comboData[LegOdds][${index}][Odds]`, legOdd.Odds.toString());
		});
	}

	// accept better odds
	formData.append('acceptBetterOdds', (user?.BetterOdds ?? false).toString());

	const uuidValue = uuid();
	formData.append('BettingId', uuidValue);

	const betAgainFlag = betAction === BetAction.BetAgain ? BetAction.BetAgain : 0;
	formData.append('BetAction', (BetAction.BetBuilderPlus | betAgainFlag).toString());

	if (useSabaCoin) {
		formData.append('isUseSabaCoin', 'true');
	}
	const options = {
		body: formData
	};

	const response = await fetchComposer.postWithBearerBase(cloudUrlParser(`${ctcdDomain}/api/Betting/DoComboTicketsBet`), options)(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
}


export const useBBPlusTicketInfo = (tickets: Array<TicketModel>) => {
	const isUseSabaCoin = useBetslipStore(state => state.useSabaCoin);
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.BBPLUS_TICKET_INFO + tickets.length],
		queryFn: () => syncBBPlusTicket(tickets, isUseSabaCoin),
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: false
	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		const updateJwt = () => {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		};

		updateJwt();
	}, [data?.jwtToken]);

	return {
		syncBBPlusTicketResponse: data?.data.Data,
		isLoading,
		isError,
		error,
		refetch
	};
};

export const useBBPlusTicketBet = () => {
	const isUseSabaCoin = useBetslipStore(state => state.useSabaCoin);
	const { user } = useAccountInfo();
	const router = useRouter();
	const ctcdDomain = useSiteStore(state => state.apiDomain.ctcdDomain);
	const [currentBBPlusTickets, currentBBPlusParlayTicketModel] = useBetslipStore(state => [
		state.ticketCarts['bb-plus'].multiTickets,
		state.ticketCarts['bb-plus'].parlayTicketModel,
	]);
	const { data, isFetching, isError, error, refetch } = useQuery({
		queryKey: [`${queryKeys.BBPLUS_TICKET_BET}_${currentBBPlusTickets.length}_${currentBBPlusParlayTicketModel?.Tickets?.length}`],
		queryFn: () => betBBPlusTicket(ctcdDomain, currentBBPlusParlayTicketModel, user, isUseSabaCoin),
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: false,
	});
	const { updateAuthToken } = useAuthActions();

	const errorData = data?.data 
		? {
			errorCode: data.data.ErrorCode,
			errorMessage: data.data.ErrorMsg
		} 
		: null;

	useEffect(() => {
		const updateJwt = () => {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		};
		updateJwt();
	}, [data?.jwtToken]);

	useEffect(() => {
		if (String(errorData?.errorCode).includes('IP-0001')) {
			router.push(`/app-error?errorcode=${errorData?.errorCode}`);
		}
	}, [errorData?.errorCode, router]);

	return {
		betTicketResponse: data?.data.Data,
		isError,
		isFetching,
		error,
		errorData,
		refetch
	};
};



